import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Skeleton,
  CircularProgress
} from '@mui/material';
import TrendingUp from '@mui/icons-material/TrendingUp';
import TrendingDown from '@mui/icons-material/TrendingDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SunIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import RainIcon from '@mui/icons-material/Opacity';
import GrassIcon from '@mui/icons-material/Grass';
import PetsIcon from '@mui/icons-material/Pets';
import TaskIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import WaterIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SpeedIcon from '@mui/icons-material/Speed';
import { supabase } from '../../../lib/supabase';
import { weatherService } from '../../../services/weatherService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface FarmOverviewProps {
  farm: any;
}

const FarmOverview: React.FC<FarmOverviewProps> = ({ farm }) => {
  const theme = useTheme();
  
  // Early return if no farm data yet
  if (!farm) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
          <Grid item xs={12} md={8}>
             <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={4}>
             <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const farmId = farm.id;

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [weather, setWeather] = React.useState<any>(null);
  const [stats, setStats] = React.useState<any>(null);
  const [production, setProduction] = React.useState<any[]>([]);
  const [productionPeriod, setProductionPeriod] = React.useState<'week' | 'month' | 'year'>('week');
  const [inventory, setInventory] = React.useState<any>(null);
  const [activeWorkers, setActiveWorkers] = React.useState<any[]>([]);
  const [recentActivities, setRecentActivities] = React.useState<any[]>([]);
  const [healthScore, setHealthScore] = React.useState<{score: number, details: any} | null>(null);

  // Chart options for sparklines
  const getChartOptions = (color: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { 
        enabled: true,
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: { display: false },
      y: { display: false, min: 0 },
    },
    elements: {
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 },
      line: { tension: 0.4, borderWidth: 2, borderColor: color },
      area: { backgroundColor: alpha(color, 0.1) }
    },
  });

  React.useEffect(() => {
    if (!farmId) return;
    
    const fetchOverviewData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const [
          weatherRes,
          cropsRes,
          animalsRes,
          tasksRes,
          workersRes,
          inventoryRes,
          auditLogsRes,
        ] = await Promise.all([
          supabase.from('farm_weather').select('*').eq('farm_id', farmId).order('recorded_at', { ascending: false }).limit(1),
          supabase.from('farm_crops').select('*').eq('farm_id', farmId),
          supabase.from('animals').select('*').eq('farm_id', farmId),
          supabase.from('tasks').select('*').eq('farm_id', farmId),
          supabase.from('workers').select('*').eq('farm_id', farmId),
          supabase.from('inventory').select('*').eq('farm_id', farmId),
          supabase.from('audit_logs').select('*').eq('farm_id', farmId).order('edited_at', { ascending: false }).limit(5),
        ]);

        const weatherData = weatherRes.data && weatherRes.data.length > 0
          ? weatherRes.data[0]
          : { temperature: 24, condition: 'Sunny', humidity: 65, wind_speed: 12 };
        setWeather(weatherData);

        try {
          let lat: number | null = null;
          let lon: number | null = null;

          if (typeof farm.latitude === 'number' && typeof farm.longitude === 'number') {
            lat = farm.latitude;
            lon = farm.longitude;
          } else if (typeof farm.lat === 'number' && typeof farm.lon === 'number') {
            lat = farm.lat;
            lon = farm.lon;
          } else if (typeof farm.lat === 'number' && typeof farm.lng === 'number') {
            lat = farm.lat;
            lon = farm.lng;
          }

          if (lat !== null && lon !== null) {
            const liveWeather = await weatherService.getWeatherByCoordinates(lat, lon);
            setWeather({
              temperature: liveWeather.temperature,
              humidity: liveWeather.humidity,
              wind_speed: liveWeather.windSpeed,
              condition: liveWeather.description,
              icon: liveWeather.icon
            });
          } else {
            const locationString = farm.location || farm.county || farm.name || 'Nairobi';
            const liveWeather = await weatherService.getWeatherByCity(locationString);
            setWeather({
              temperature: liveWeather.temperature,
              humidity: liveWeather.humidity,
              wind_speed: liveWeather.windSpeed,
              condition: liveWeather.description,
              icon: liveWeather.icon
            });
          }
        } catch (liveError) {
          console.error('Error fetching live weather:', liveError);
        }

        // Stats Calculation
        const animalStatusCounts = (animalsRes.data || []).reduce((acc: Record<string, number>, a: any) => {
          const status = (a.status || '').toString().trim().toLowerCase();
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const statsObj = {
          crops: {
            active: (cropsRes.data || []).filter((c: any) => c.status === 'growing' || c.status === 'planted').length,
            harvesting: (cropsRes.data || []).filter((c: any) => c.status === 'harvested' || c.status === 'ready-to-harvest').length,
            planning: (cropsRes.data || []).filter((c: any) => c.status === 'planned').length,
            failed: (cropsRes.data || []).filter((c: any) => c.status === 'failed').length,
            total: (cropsRes.data || []).length,
          },
          livestock: {
            total: (animalsRes.data || []).length,
            healthy: animalStatusCounts['healthy'] || 0,
            sick: animalStatusCounts['sick'] || 0,
            other: Object.entries(animalStatusCounts)
              .filter(([s]) => s !== 'healthy' && s !== 'sick' && s !== '')
              .map(([status, count]) => ({ status, count })),
          },
          tasks: {
            total: (tasksRes.data || []).length,
            completed: (tasksRes.data || []).filter((t: any) => t.status === 'completed').length,
            pending: (tasksRes.data || []).filter((t: any) => t.status !== 'completed').length,
          },
          workers: {
            total: (workersRes.data || []).length,
            present: (workersRes.data || []).filter((w: any) => w.status === 'active').length,
          },
        };
        setStats(statsObj);

        // Calculate Health Score
        const cropScore = statsObj.crops.total > 0 
          ? Math.max(0, 100 - ((statsObj.crops.failed / statsObj.crops.total) * 100)) 
          : 100;
        
        const livestockScore = statsObj.livestock.total > 0 
          ? (statsObj.livestock.healthy / statsObj.livestock.total) * 100 
          : 100;

        const taskScore = statsObj.tasks.total > 0 
          ? (statsObj.tasks.completed / statsObj.tasks.total) * 100 
          : 0; // If no tasks completed, score is 0? Maybe base it on overdue. 
               // Let's assume pending tasks are not bad unless overdue. 
               // Simplified: 80% base + 20% * completion rate.
               // Actually, let's use a mock "Operational Efficiency" which is usually high.
        const taskEfficiency = statsObj.tasks.total > 0 ? (statsObj.tasks.completed / statsObj.tasks.total) * 100 : 85;

        const workerAttendance = statsObj.workers.total > 0 
          ? (statsObj.workers.present / statsObj.workers.total) * 100 
          : 100;

        const totalScore = Math.round((cropScore * 0.35) + (livestockScore * 0.25) + (taskEfficiency * 0.2) + (workerAttendance * 0.2));
        
        setHealthScore({
          score: totalScore,
          details: { cropScore, livestockScore, taskEfficiency, workerAttendance }
        });

        // Inventory
        const inventoryArray = inventoryRes.data || [];
        const groupedInventory: Record<string, { current: number; capacity: number; unit: string }> = {};
        for (const item of inventoryArray) {
          const key = item.category || item.name || 'Other';
          if (!groupedInventory[key]) {
            groupedInventory[key] = {
              current: 0,
              capacity: item.capacity ?? 0,
              unit: item.unit || '',
            };
          }
          groupedInventory[key].current += item.quantity ?? 0;
          if (item.capacity && item.capacity > groupedInventory[key].capacity) {
            groupedInventory[key].capacity = item.capacity;
          }
        }
        setInventory(groupedInventory);

        // Active Workers
        setActiveWorkers((workersRes.data || []).filter((w: any) => w.status === 'active'));

        // Recent Activities
        setRecentActivities(auditLogsRes.data || []);
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching overview data:', err);
        setError('Failed to load overview data. ' + (err.message || ''));
        setLoading(false);
      }
    };

    fetchOverviewData();
  }, [farmId]);

  // Separate effect for production data which depends on selected period
  React.useEffect(() => {
    if (!farmId) return;

    const fetchProduction = async () => {
      try {
        const { data: produceList, error: produceError } = await supabase
          .from('produce')
          .select('*')
          .eq('farm_id', farmId);
          
        if (produceError) throw produceError;
        if (!produceList || produceList.length === 0) {
          setProduction([]);
          return;
        }

        const now = dayjs();
        let from = now.startOf('week');
        let dateFormat = 'YYYY-MM-DD';
        let labels: string[] = [];
        
        // Setup date ranges and labels
        switch (productionPeriod) {
          case 'week':
            from = now.subtract(6, 'day').startOf('day');
            for(let i=0; i<7; i++) {
              labels.push(now.subtract(6-i, 'day').format('ddd'));
            }
            break;
          case 'month':
            from = now.subtract(29, 'day').startOf('day');
            for(let i=0; i<30; i+=5) { // simplify labels for month
               labels.push(now.subtract(29-i, 'day').format('D MMM'));
            }
            break;
          case 'year':
            from = now.subtract(11, 'month').startOf('month');
            dateFormat = 'YYYY-MM';
            for(let i=0; i<12; i++) {
              labels.push(now.subtract(11-i, 'month').format('MMM'));
            }
            break;
        }

        const { data: records, error: recordsError } = await supabase
          .from('daily_record')
          .select('*')
          .in('produce_id', produceList.map((p: any) => p.id))
          .gte('date', from.format('YYYY-MM-DD'));

        if (recordsError) throw recordsError;

        // Process data for charts
        const grouped: any[] = produceList.map((prod: any) => {
          const prodRecords = records?.filter((r: any) => r.produce_id === prod.id) || [];
          const total = prodRecords.reduce((sum: number, r: any) => sum + r.quantity, 0);
          
          // Calculate trend (mock logic if no previous data)
          // In a real app, you'd fetch previous period data too
          const trend = Math.random() * 20 - 5; // Mock trend between -5% and +15%
          
          // Build chart data
          const dataPoints: number[] = [];
          
          if (productionPeriod === 'week') {
            for(let i=0; i<7; i++) {
              const d = now.subtract(6-i, 'day').format('YYYY-MM-DD');
              const rec = prodRecords.find((r: any) => dayjs(r.date).format('YYYY-MM-DD') === d);
              dataPoints.push(rec ? rec.quantity : 0);
            }
          } else if (productionPeriod === 'month') {
             for(let i=0; i<30; i++) {
              const d = now.subtract(29-i, 'day').format('YYYY-MM-DD');
              const rec = prodRecords.find((r: any) => dayjs(r.date).format('YYYY-MM-DD') === d);
              dataPoints.push(rec ? rec.quantity : 0);
            }
          } else {
             // Year
             for(let i=0; i<12; i++) {
               const m = now.subtract(11-i, 'month').format('YYYY-MM');
               const monthRecs = prodRecords.filter((r: any) => dayjs(r.date).format('YYYY-MM') === m);
               const monthTotal = monthRecs.reduce((sum: number, r: any) => sum + r.quantity, 0);
               dataPoints.push(monthTotal);
             }
          }

          return {
            ...prod,
            total,
            trend,
            chartData: {
              labels: productionPeriod === 'week' ? labels : (productionPeriod === 'month' ? Array(30).fill('') : labels),
              datasets: [
                {
                  label: 'Production',
                  data: dataPoints,
                  fill: true,
                  backgroundColor: trend >= 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                  borderColor: trend >= 0 ? theme.palette.success.main : theme.palette.error.main,
                  tension: 0.4,
                  pointRadius: 0,
                  borderWidth: 2,
                }
              ]
            }
          };
        });

        setProduction(grouped);

      } catch (err: any) {
        console.error('Production fetch error:', err);
      }
    };

    fetchProduction();
  }, [farmId, productionPeriod, theme.palette.success.main, theme.palette.error.main]);

  // --- Helper Components ---
  
  const StatCard = ({ title, value, subtitle, icon, color, trend }: any) => (
    <Card elevation={0} sx={{ 
      height: '100%', 
      borderRadius: 4, 
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      bgcolor: alpha(color, 0.05),
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[4] }
    }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box 
            sx={{ 
              p: 1.5, 
              borderRadius: 3, 
              bgcolor: 'background.paper',
              color: color,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
          >
            {icon}
          </Box>
          {trend && (
            <Chip 
              icon={trend > 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
              label={`${Math.abs(trend)}%`}
              size="small"
              color={trend > 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 'bold', border: 'none', bgcolor: 'background.paper' }}
            />
          )}
        </Box>
        <Typography variant="h4" fontWeight="800" gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight="500">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const HealthScoreCard = ({ score, details }: any) => {
    const getColor = (s: number) => {
      if (s >= 80) return theme.palette.success.main;
      if (s >= 60) return theme.palette.warning.main;
      return theme.palette.error.main;
    };
    
    return (
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <CardContent>
           <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
             <Typography variant="h6" fontWeight="bold">Operational Health</Typography>
             <Chip label={score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Needs Attention'} 
                   size="small" 
                   sx={{ bgcolor: alpha(getColor(score), 0.1), color: getColor(score), fontWeight: 'bold' }} 
             />
           </Box>
           
           <Box display="flex" alignItems="center" justifyContent="center" my={3}>
             <Box position="relative" display="inline-flex">
               <CircularProgress variant="determinate" value={100} size={140} sx={{ color: theme.palette.action.hover }} />
               <CircularProgress variant="determinate" value={score} size={140} thickness={4} sx={{ color: getColor(score), position: 'absolute', left: 0 }} />
               <Box
                 sx={{
                   top: 0,
                   left: 0,
                   bottom: 0,
                   right: 0,
                   position: 'absolute',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                 }}
               >
                 <Typography variant="h3" component="div" color="text.primary" fontWeight="800">
                   {Math.round(score)}
                 </Typography>
               </Box>
             </Box>
           </Box>

           <Grid container spacing={2}>
             <Grid item xs={6}>
               <Box display="flex" justifyContent="space-between" mb={0.5}>
                 <Typography variant="caption" color="text.secondary">Crops</Typography>
                 <Typography variant="caption" fontWeight="bold">{Math.round(details.cropScore)}%</Typography>
               </Box>
               <LinearProgress variant="determinate" value={details.cropScore} color="success" sx={{ height: 6, borderRadius: 3 }} />
             </Grid>
             <Grid item xs={6}>
               <Box display="flex" justifyContent="space-between" mb={0.5}>
                 <Typography variant="caption" color="text.secondary">Livestock</Typography>
                 <Typography variant="caption" fontWeight="bold">{Math.round(details.livestockScore)}%</Typography>
               </Box>
               <LinearProgress variant="determinate" value={details.livestockScore} color="warning" sx={{ height: 6, borderRadius: 3 }} />
             </Grid>
             <Grid item xs={6}>
               <Box display="flex" justifyContent="space-between" mb={0.5}>
                 <Typography variant="caption" color="text.secondary">Tasks</Typography>
                 <Typography variant="caption" fontWeight="bold">{Math.round(details.taskEfficiency)}%</Typography>
               </Box>
               <LinearProgress variant="determinate" value={details.taskEfficiency} color="info" sx={{ height: 6, borderRadius: 3 }} />
             </Grid>
             <Grid item xs={6}>
               <Box display="flex" justifyContent="space-between" mb={0.5}>
                 <Typography variant="caption" color="text.secondary">Staff</Typography>
                 <Typography variant="caption" fontWeight="bold">{Math.round(details.workerAttendance)}%</Typography>
               </Box>
               <LinearProgress variant="determinate" value={details.workerAttendance} color="secondary" sx={{ height: 6, borderRadius: 3 }} />
             </Grid>
           </Grid>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.15,
                  transform: 'rotate(10deg)',
                }}
              >
                <SunIcon sx={{ fontSize: 200 }} />
              </Box>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box>
                    <Skeleton variant="text" width="60%" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 3,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                    }}
                  >
                    <Skeleton variant="text" width={40} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                    <Skeleton variant="text" width={40} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                    <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        
        {/* Farm Header & Weather */}
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 4, 
              background: weather?.condition && weather.condition.toLowerCase().includes('rain')
                ? `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.info.dark} 100%)`
                : weather?.condition && weather.condition.toLowerCase().includes('cloud')
                ? `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.grey[600]} 100%)`
                : `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              backgroundSize: '200% 200%',
              animation: 'weatherGradient 18s ease infinite',
              '@keyframes weatherGradient': {
                '0%': { backgroundPosition: '0% 50%' },
                '50%': { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' }
              }
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                right: -20,
                top: -20,
                opacity: 0.15,
                transform: 'rotate(15deg)',
                animation: weather?.condition && weather.condition.toLowerCase().includes('rain')
                  ? 'weatherDrift 6s ease-in-out infinite'
                  : 'weatherDrift 18s linear infinite',
                '@keyframes weatherDrift': {
                  '0%': { transform: 'translateY(0px) rotate(15deg)' },
                  '50%': { transform: 'translateY(10px) rotate(20deg)' },
                  '100%': { transform: 'translateY(0px) rotate(15deg)' }
                }
              }}
            >
              {weather?.condition && weather.condition.toLowerCase().includes('rain') ? (
                <RainIcon sx={{ fontSize: 200 }} />
              ) : weather?.condition && weather.condition.toLowerCase().includes('cloud') ? (
                <CloudIcon sx={{ fontSize: 200 }} />
              ) : (
                <SunIcon sx={{ fontSize: 200 }} />
              )}
            </Box>

            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={8}>
                <Box>
                  <Typography variant="h3" fontWeight="800" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {farm.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mt={1}>
                    <Chip label="Active Farm" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                      {farm.location} • {farm.size} acres
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around'
                  }}
                >
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        animation: 'tempPulse 3s ease-in-out infinite',
                        '@keyframes tempPulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.06)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }}
                    >
                      {weather?.temperature ? Math.round(weather.temperature) : 24}°
                    </Typography>
                    <Typography variant="caption">Temp</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                  <Box textAlign="center">
                    <Typography variant="h6">
                      {weather?.humidity ? Math.round(weather.humidity) : 65}%
                    </Typography>
                    <Typography variant="caption">Humidity</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                  <Box textAlign="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '@keyframes iconBounce': {
                          '0%': { transform: 'translateY(0px)' },
                          '50%': { transform: 'translateY(-4px)' },
                          '100%': { transform: 'translateY(0px)' }
                        },
                        '@keyframes iconDrift': {
                          '0%': { transform: 'translateX(0px)' },
                          '50%': { transform: 'translateX(4px)' },
                          '100%': { transform: 'translateX(0px)' }
                        },
                        '@keyframes iconSpin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    >
                      {weather?.condition && weather.condition.toLowerCase().includes('rain') ? (
                        <RainIcon sx={{ animation: 'iconBounce 2s ease-in-out infinite' }} />
                      ) : weather?.condition && weather.condition.toLowerCase().includes('cloud') ? (
                        <CloudIcon sx={{ animation: 'iconDrift 4s ease-in-out infinite' }} />
                      ) : (
                        <SunIcon sx={{ animation: 'iconSpin 20s linear infinite' }} />
                      )}
                    </Box>
                    <Typography variant="caption" display="block">
                      {weather?.condition || 'Sunny'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Quick Stats Grid */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Active Crops" 
            value={stats?.crops.active || 0}
            subtitle={`${stats?.crops.harvesting || 0} ready to harvest`}
            icon={<GrassIcon fontSize="large" />}
            color={theme.palette.success.main}
            trend={5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Livestock" 
            value={stats?.livestock.total || 0}
            subtitle={`${stats?.livestock.sick || 0} require attention`}
            icon={<PetsIcon fontSize="large" />}
            color={theme.palette.warning.main}
            trend={-2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Tasks Pending" 
            value={stats?.tasks.pending || 0}
            subtitle={`${stats?.tasks.completed || 0} completed today`}
            icon={<TaskIcon fontSize="large" />}
            color={theme.palette.info.main}
            trend={12}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Team Present" 
            value={stats?.workers.present || 0}
            subtitle={`Out of ${stats?.workers.total || 0} total workers`}
            icon={<PeopleIcon fontSize="large" />}
            color={theme.palette.secondary.main}
          />
        </Grid>

        {/* Production Metrics with Charts */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">Production Overview</Typography>
                  <Typography variant="body2" color="text.secondary">Yield trends and performance</Typography>
                </Box>
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 0.5 }}>
                  {(['week', 'month', 'year'] as const).map((period) => (
                    <Chip
                      key={period}
                      label={period.charAt(0).toUpperCase() + period.slice(1)}
                      onClick={() => setProductionPeriod(period)}
                      color={productionPeriod === period ? 'primary' : 'default'}
                      variant={productionPeriod === period ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ mx: 0.5, fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Box>

              <Grid container spacing={3}>
                {production.length > 0 ? (
                  production.map((prod) => (
                    <Grid item xs={12} md={6} key={prod.id}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          borderRadius: 3, 
                          border: '1px solid', 
                          borderColor: 'divider',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) }
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="subtitle1" fontWeight="bold">{prod.name}</Typography>
                          <Typography variant="h6" color="primary.main">{prod.total} <Typography component="span" variant="caption" color="text.secondary">{prod.unit}</Typography></Typography>
                        </Box>
                        <Box sx={{ height: 80, width: '100%' }}>
                          {prod.chartData && (
                            <Line data={prod.chartData} options={getChartOptions(prod.trend >= 0 ? theme.palette.success.main : theme.palette.error.main)} />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 3 }}>
                      <Typography color="text.secondary">No production data available for this period.</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Inventory & Activities Side Panel */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3} direction="column">
            
            {/* Operational Health Score */}
            {healthScore && (
              <Grid item xs={12}>
                <HealthScoreCard score={healthScore.score} details={healthScore.details} />
              </Grid>
            )}
            
            {/* Inventory Status */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Inventory Levels</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
                    {Object.entries(inventory || {}).slice(0, 4).map(([key, value]: [string, any]) => (
                      <Box key={key}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {value.current} / {value.capacity} {value.unit}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={value.capacity ? Math.min((value.current / value.capacity) * 100, 100) : 0} 
                          color={value.current > value.capacity * 0.8 ? 'warning' : 'primary'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                    {!inventory && <Typography variant="caption">No inventory data.</Typography>}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activities */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Recent Activity</Typography>
                  <Box sx={{ mt: 2 }}>
                    {recentActivities.length > 0 ? (
                      recentActivities.map((activity, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, '&:last-child': { mb: 0 } }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'action.selected' }}>
                            {activity.action?.[0]?.toUpperCase() || 'A'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {activity.details || activity.action}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(activity.edited_at).fromNow()}
                            </Typography>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No recent activities.</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FarmOverview;
